import React from 'react';
import { useAuth } from '../hooks/useAuth';
import { RoleService } from '../services/roleService';
import { ApiProvider, UserRole } from '../types';

interface RoleGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  allowedRoles?: UserRole[];
  requireProvider?: ApiProvider;
  requireImageEnhancer?: boolean;
}

/**
 * Role-based access control component
 * Conditionally renders children based on user authentication and permissions
 */
export const RoleGuard: React.FC<RoleGuardProps> = ({
  children,
  fallback = null,
  allowedRoles,
  requireProvider,
  requireImageEnhancer,
}) => {
  const { user, isAuthenticated } = useAuth();

  // If not authenticated, always show fallback
  if (!isAuthenticated || !user) {
    return <>{fallback}</>;
  }

  const userPermissions = RoleService.getPermissions(user);

  // Check if user's role is allowed
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <>{fallback}</>;
  }

  // Check if a specific API provider is required
  if (requireProvider && !userPermissions.allowedProviders.includes(requireProvider)) {
    return <>{fallback}</>;
  }

  // Check if image enhancer is required
  if (requireImageEnhancer && !userPermissions.canUseImageEnhancer) {
    return <>{fallback}</>;
  }

  // All checks passed, render children
  return <>{children}</>;
};

export default RoleGuard;
